/* Context + provider: fast-refresh prefers components-only files. */
/* eslint-disable react-refresh/only-export-components */
import {type PopperProps} from "@mui/material";
import {
    createContext,
    useCallback,
    useMemo,
    useState,
    type ReactNode,
} from "react";

export type EntityPlaceholderContextValue = {
    autoOpenNodeKey: string | null;
    clearAutoOpen: () => void;
    /** Used when inserting a prompt with `[...]` slots to open the first entity’s picker once. */
    setAutoOpenNodeKey: (key: string | null) => void;
    openPlacement: Pick<PopperProps, "placement">;
};

const defaultEntityPlaceholderContext: EntityPlaceholderContextValue = {
    autoOpenNodeKey: null,
    clearAutoOpen: () => {},
    setAutoOpenNodeKey: () => {},
    openPlacement: {placement: "bottom-start"},
};

export const EntityPlaceholderContext =
    createContext<EntityPlaceholderContextValue>(defaultEntityPlaceholderContext);

type EntityPlaceholderProviderProps = {
    children: ReactNode;
    openPlacement: Pick<PopperProps, "placement">;
};

export function EntityPlaceholderProvider({
                                              children,
                                              openPlacement,
                                          }: EntityPlaceholderProviderProps) {
    const [autoOpenNodeKey, setAutoOpenNodeKey] = useState<string | null>(null);
    const clearAutoOpen = useCallback(() => {
        setAutoOpenNodeKey(null);
    }, []);

    const value = useMemo(
        () => ({
            autoOpenNodeKey,
            openPlacement,
            clearAutoOpen,
            setAutoOpenNodeKey,
        }),
        [autoOpenNodeKey, clearAutoOpen, openPlacement],
    );

    return (
        <EntityPlaceholderContext.Provider value={value}>
            {children}
        </EntityPlaceholderContext.Provider>
    );
}
